// "Arbeitet für" in der Schnellerfassung (ST-REL-001, Verknuepfung in der
// Schnellerfassung; CM-REL-001).
//
// FELD UND EINE KARTE (NFR-UX-004): unter dem Feld steht hoechstens eine
// Karte -- Treffer, "Neu", "Vorhanden" oder "Suche gescheitert". Gesucht wird
// ohne eigenen Knopf: sofort mit dem Namen aus einer Signatur, sonst beim
// Verlassen des Felds und mit Enter, aber nur, wenn sich der Text seit der
// letzten Suche geaendert hat (sonst suchte der Tab in die Karte noch einmal).
// Ohne Signatur bleibt der Abschnitt zugeklappt. Die Hauptaktion des
// Bildschirms bleibt "Kontakt anlegen"; die Verknuepfung entsteht mit ihr.
//
// NUR VORHANDENE, SICHTBARE ORGANISATIONEN: die Treffer kommen aus der
// bestehenden Suche (ADR-0049) und werden auf Organisationen gefiltert. Ob die
// Wahl beim Anlegen noch taugt, entscheidet der Server; die Oberflaeche ist die
// freundlichere Kontrolle, nicht die Grenze.
//
// NEU ANLEGEN nur nach einer Suche ohne weitere Treffer und nur fuer genau den
// gesuchten Text: die Suche ist die Dublettenpruefung. Vorausgewaehlt ist "Neu"
// nur, wenn der Text unveraendert der Name aus der Signatur ist; sonst bietet
// die Karte es an. Unsichtbare Organisationen anderer Haeuser bleiben
// unsichtbar: das Angebot sagt nichts, was die Suche nicht schon sagt
// (CM-SEC-003).
//
// BARRIEREFREIHEIT (NFR-ACC-001): der Ausgang einer Suche wird ueber aria-live
// angesagt -- OHNE role="status", denn die eine Statusregion des Bildschirms
// gehoert dem Ergebnis der Anlage. Fokus: Waehlen -> Karte, Aendern -> Feld,
// "neu anlegen" -> "Doch nicht anlegen", "Doch nicht anlegen" -> der Knopf,
// der es wieder anbietet.

import { useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { Badge, Body1, Button, Caption1, Field, Input, makeStyles, tokens } from '@fluentui/react-components';

import { searchParties, searchTermProblem, type PartySummary } from '../parties.ts';
import {
  createOrganizationLabel,
  inputIdFor,
  NEW_ORGANIZATION_CARD,
  noneOfTheseLabel,
  WORKS_FOR_MORE_HITS,
  WORKS_FOR_NO_MATCH,
  WORKS_FOR_SEARCH_UNREACHABLE,
  worksForHitCaption,
  worksForHitsHeading,
  worksForNoMatchAnnouncement,
  type WorksForChoice,
} from '../quick-capture.ts';

const rahmen = {
  marginTop: '0.5rem',
  padding: '0.75rem',
  border: `1px solid ${tokens.colorNeutralStroke2}`,
  borderRadius: tokens.borderRadiusMedium,
} as const;

// Fluent-Knoepfe umbrechen nicht und sind schmal begrenzt; ein langer
// Organisationsname liefe bei 320 px ueber (WCAG 1.4.10, NFR-ACC-001).
const useStyles = makeStyles({
  umbrechend: { whiteSpace: 'normal', height: 'auto', maxWidth: '100%', textAlign: 'start', justifyContent: 'flex-start' },
  // v4 .orgcard: eine ruhige Karte mit Zustand links und Handlung rechts, umbrechend bei 320 px.
  karte: { ...rahmen, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem 0.75rem' },
  karteSpalte: { ...rahmen, display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  karteText: { display: 'flex', flexDirection: 'column', gap: '0.125rem', minWidth: 0, overflowWrap: 'anywhere' },
  name: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.25rem 0.5rem' },
  gedaempft: { color: tokens.colorNeutralForeground2 },
  treffer: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  zeile: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '0.25rem 0.75rem' },
  nurVorgelesen: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    margin: '-1px',
    padding: 0,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
});

const WORKS_FOR_SEARCH_INPUT_ID = inputIdFor('worksForPartyId') ?? 'qc-arbeitet-fuer-suche';

type Ergebnis =
  | { readonly art: 'treffer'; readonly begriff: string; readonly organisationen: readonly PartySummary[]; readonly mehr: boolean }
  | { readonly art: 'gescheitert'; readonly satz: string };

export function ArbeitetFuer({
  apiBaseUrl,
  accessToken,
  onReauthenticate,
  value,
  onChange,
  error,
  disabled,
  vorschlag,
  neu,
  onNeuChange,
  angabenNeu,
  leitungNeu,
  leitungVorhanden,
}: {
  readonly apiBaseUrl: string;
  readonly accessToken: () => Promise<string | undefined>;
  readonly onReauthenticate: () => void;
  readonly value: WorksForChoice | undefined;
  readonly onChange: (choice: WorksForChoice | undefined) => void;
  /** Die Ablehnung des Servers am Feld; sie oeffnet das Feld und holt den Fokus. */
  readonly error: string | null;
  readonly disabled: boolean;
  /** Organisationsname aus der eingefuegten Signatur; nur ein Suchbegriff, keine Zuordnung. */
  readonly vorschlag?: string | undefined;
  /** Der Name, der beim Anlegen als neue Organisation mitentsteht; sonst `undefined`. */
  readonly neu: string | undefined;
  readonly onNeuChange: (name: string | undefined) => void;
  /** "Angaben zur Organisation" (S2): steht in der Karte "Neu" unter Name und Rueckweg. */
  readonly angabenNeu?: ReactNode;
  /** Die erkannte Leitung als Vorschlag (S3, V5): unter den Angaben, in derselben Karte. */
  readonly leitungNeu?: ReactNode;
  /** Die Leitung in der Karte "Vorhanden": nach dem Teilerfolg O4 die Wahl (V5), sonst der Abgleich (V8). */
  readonly leitungVorhanden?: ReactNode;
}): ReactElement {
  const styles = useStyles();
  const [offen, setOffen] = useState(false);
  const [begriff, setBegriff] = useState('');
  /** Der zuletzt gesuchte Text; Verlassen und Enter suchen nur bei einem anderen. */
  const [gesucht, setGesucht] = useState<string | undefined>(undefined);
  const [ergebnis, setErgebnis] = useState<Ergebnis | undefined>(undefined);
  const [ansage, setAnsage] = useState('');
  const [sucheFehler, setSucheFehler] = useState<string | undefined>(undefined);
  const [sucht, setSucht] = useState(false);
  /** Die gewaehlte Organisation mit ihrer Zeile, fuer die Karte "Vorhanden". */
  const [gewaehlt, setGewaehlt] = useState<PartySummary | undefined>(undefined);
  const laufRef = useRef(0);
  const karteRef = useRef<HTMLDivElement | null>(null);
  const oeffnenRef = useRef<HTMLDivElement | null>(null);
  const anbietenRef = useRef<HTMLButtonElement | null>(null);
  const zuruecknehmenRef = useRef<HTMLButtonElement | null>(null);
  const [fokus, setFokus] = useState<'feld' | 'karte' | 'oeffnen' | 'anbieten' | 'neu' | undefined>(undefined);

  const feldSichtbar = value === undefined && (offen || error !== null);

  useEffect(() => {
    if (fokus === 'feld') document.getElementById(WORKS_FOR_SEARCH_INPUT_ID)?.focus();
    if (fokus === 'karte') karteRef.current?.focus();
    if (fokus === 'oeffnen') oeffnenRef.current?.querySelector('button')?.focus();
    if (fokus === 'anbieten') anbietenRef.current?.focus();
    if (fokus === 'neu') zuruecknehmenRef.current?.focus();
    if (fokus !== undefined) setFokus(undefined);
  }, [fokus]);

  // Beginnt das Absenden, gilt eine laufende Suche nicht mehr: ihre Antwort kaeme nach der
  // Entscheidung des Formulars und setzte sonst ein "Neu", das diese Anlage nicht mehr sieht.
  useEffect(() => {
    if (!disabled || !sucht) return;
    laufRef.current += 1;
    setSucht(false);
    setGesucht(undefined);
    setAnsage('');
  }, [disabled, sucht]);

  // Eine Ablehnung des Servers: das Feld steht offen, der Fokus geht hinein.
  useEffect(() => {
    if (error === null) return;
    setOffen(true);
    setFokus('feld');
  }, [error]);

  useEffect(
    () => () => {
      laufRef.current += 1;
      // Ohne den Baustein (Art "Organisation") gibt es keine Karte "Neu", also auch kein Mitanlegen.
      onNeuChange(undefined);
    },
    // Nur beim Aushaengen; `onNeuChange` ist der Setter des Bildschirms und bleibt derselbe.
    [],
  );

  const suchen = async (roh: string): Promise<void> => {
    const term = roh.trim();
    const problem = searchTermProblem(term);
    if (problem !== undefined) {
      setSucheFehler(problem);
      return;
    }
    const lauf = laufRef.current + 1;
    laufRef.current = lauf;
    setSucheFehler(undefined);
    setGesucht(term);
    setErgebnis(undefined);
    onNeuChange(undefined);
    setSucht(true);
    setAnsage('Suche läuft.');
    try {
      const token = await accessToken();
      if (laufRef.current !== lauf) return;
      if (token === undefined) {
        setSucht(false);
        setAnsage('');
        setGesucht(undefined);
        onReauthenticate();
        return;
      }
      const antwort = await searchParties({ baseUrl: apiBaseUrl, accessToken: token, term });
      if (laufRef.current !== lauf) return;
      setSucht(false);
      if (antwort.outcome === 'rejected') {
        const satz = `${antwort.message.headline} ${antwort.message.nextStep}`;
        // Gescheitert ist keine Antwort auf den Text: derselbe Text darf erneut suchen.
        setGesucht(undefined);
        setErgebnis({ art: 'gescheitert', satz });
        setAnsage(satz);
        return;
      }
      const organisationen = antwort.parties.filter((eintrag) => eintrag.kind === 'organization');
      setErgebnis({ art: 'treffer', begriff: term, organisationen, mehr: antwort.hasMore });
      if (organisationen.length > 0) {
        setAnsage(worksForHitsHeading(organisationen.length));
        return;
      }
      // Vorausgewaehlt nur, wenn der Text unveraendert der Name aus der Signatur ist und nichts dahinter steht.
      const vorausgewaehlt = !antwort.hasMore && vorschlag !== undefined && term === vorschlag.trim();
      if (vorausgewaehlt) onNeuChange(term);
      setAnsage(antwort.hasMore ? `${WORKS_FOR_NO_MATCH} ${WORKS_FOR_MORE_HITS}` : worksForNoMatchAnnouncement(vorausgewaehlt ? term : undefined));
    } catch {
      if (laufRef.current !== lauf) return;
      setSucht(false);
      setGesucht(undefined);
      setErgebnis({ art: 'gescheitert', satz: WORKS_FOR_SEARCH_UNREACHABLE });
      setAnsage(WORKS_FOR_SEARCH_UNREACHABLE);
    }
  };

  // Mit einer Signatur steht das Feld offen, mit dem Namen gefuellt, und die Suche laeuft sofort.
  const vorschlagRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const name = vorschlag?.trim();
    const bisher = vorschlagRef.current;
    if (name === undefined || name === '') {
      // Der Name ist aus der Signatur verschwunden: was er ins Feld gebracht hat, faellt mit weg,
      // vor allem ein vorausgewaehltes "Neu" -- sonst legte die Anlage eine Organisation an, die nirgends mehr steht.
      vorschlagRef.current = undefined;
      if (bisher === undefined || value !== undefined) return;
      if (neu === bisher) onNeuChange(undefined);
      if (begriff.trim() === bisher) {
        laufRef.current += 1;
        setSucht(false);
        setBegriff('');
        setGesucht(undefined);
        setErgebnis(undefined);
        setAnsage('');
        setOffen(false);
      }
      return;
    }
    if (name === bisher) return;
    vorschlagRef.current = name;
    if (value !== undefined) return;
    setOffen(true);
    setBegriff(name);
    void suchen(name);
    // Nur ein neuer Vorschlag loest aus; `suchen` liest Props und Setter.
  }, [vorschlag]);

  /** "neu anlegen": die Karte "Neu" ersetzt den Knopf; der Fokus geht auf ihren Rueckweg, nie auf BODY. */
  const neuAnlegen = (name: string): void => {
    onNeuChange(name);
    setAnsage(worksForNoMatchAnnouncement(name).replace('Keine passende Organisation gefunden. ', ''));
    setFokus('neu');
  };

  const sucheBeiAenderung = (): void => {
    const term = begriff.trim();
    // Unter drei Zeichen beim Verlassen: kein Fehler, keine Suche; nur Enter sagt es am Feld.
    if (term === gesucht || searchTermProblem(term) !== undefined) return;
    void suchen(term);
  };

  const waehlen = (eintrag: PartySummary): void => {
    // Aus der Suche nach dem Signaturnamen gewaehlt: der Name gilt dann als uebernommen.
    const ausVorschlag = vorschlag !== undefined && gesucht === vorschlag.trim();
    onChange({ partyId: eintrag.partyId, displayName: eintrag.displayName, ...(ausVorschlag ? { fromSuggestion: vorschlag.trim() } : {}) });
    onNeuChange(undefined);
    setGewaehlt(eintrag);
    setAnsage('');
    // Erledigt: wird die Wahl verbraucht (Anlage) oder entfernt, steht der Abschnitt wieder zugeklappt da.
    setOffen(false);
    setErgebnis(undefined);
    setFokus('karte');
  };

  // Die Ansagezone steht in jedem Zustand an derselben Stelle: eine erst mit dem Text
  // eingehaengte Region sagt der Screenreader oft nicht an (etwa beim Oeffnen mit Signatur).
  const mitAnsage = (inhalt: ReactElement): ReactElement => (
    <div>
      {inhalt}
      <div aria-live="polite" className={styles.nurVorgelesen}>
        {ansage}
      </div>
    </div>
  );

  if (value !== undefined) {
    const caption = gewaehlt?.partyId === value.partyId ? worksForHitCaption(gewaehlt) : undefined;
    return mitAnsage(
      <div ref={karteRef} tabIndex={-1} role="group" aria-label={`Arbeitet für ${value.displayName}`} className={styles.karte}>
        <div className={styles.karteText}>
          <Body1 className={styles.name}>
            <strong>{value.displayName}</strong>
            {/* In Worten, nicht als Farbe allein (WCAG 1.4.1). */}
            <Badge appearance="outline" color="informative">
              Vorhanden
            </Badge>
          </Body1>
          {caption === undefined ? undefined : <Caption1 className={styles.gedaempft}>{caption}</Caption1>}
          <Caption1 className={styles.gedaempft}>
            Die Beziehung entsteht mit dem Kontakt und beginnt heute. Sehen können sie alle, die beide Kontakte öffnen dürfen.
          </Caption1>
          {leitungVorhanden}
        </div>
        <Button
          appearance="subtle"
          type="button"
          disabled={disabled}
          onClick={() => {
            // Der Name steht im Feld; Verlassen oder Enter zeigt die Treffer wieder.
            setBegriff(value.displayName);
            setGesucht(undefined);
            onChange(undefined);
            setGewaehlt(undefined);
            setOffen(true);
            setFokus('feld');
          }}
        >
          Ändern
        </Button>
      </div>,
    );
  }

  if (!feldSichtbar) {
    return mitAnsage(
      <div ref={oeffnenRef} className={styles.karte}>
        <div className={styles.karteText}>
          <Body1 className={styles.gedaempft}>Keine Organisation</Body1>
        </div>
        {/* Secondary, nicht primary: "Anlegen" bleibt die einzige Hauptaktion der Seite. */}
        <Button
          appearance="secondary"
          type="button"
          className={styles.umbrechend}
          disabled={disabled}
          onClick={() => {
            setOffen(true);
            setFokus('feld');
          }}
        >
          Organisation zuordnen
        </Button>
      </div>,
    );
  }

  // Die Karte gilt nur dem Text, der gesucht wurde; ein anderer Text wartet auf seine Suche.
  const aktuell = ergebnis?.art === 'treffer' && ergebnis.begriff === begriff.trim() ? ergebnis : undefined;

  let karte: ReactElement | undefined;
  if (sucht) {
    karte = <Caption1 className={styles.gedaempft}>Suche läuft.</Caption1>;
  } else if (ergebnis?.art === 'gescheitert') {
    // Ohne Suche keine Dublettenpruefung: also auch kein "Neu".
    karte = (
      <div className={styles.karte}>
        <Body1>{ergebnis.satz}</Body1>
      </div>
    );
  } else if (aktuell !== undefined && neu === aktuell.begriff) {
    karte = (
      <div className={styles.karteSpalte}>
        <div className={styles.zeile}>
          <div className={styles.karteText}>
            <Body1 className={styles.name}>
              <strong>{aktuell.begriff}</strong>
              <Badge appearance="outline" color="brand">
                Neu
              </Badge>
            </Body1>
            <Caption1 className={styles.gedaempft}>{NEW_ORGANIZATION_CARD}</Caption1>
          </div>
          <Button
            ref={(knoten: HTMLButtonElement | null) => {
              zuruecknehmenRef.current = knoten;
            }}
            appearance="subtle"
            type="button"
            disabled={disabled}
            onClick={() => {
              onNeuChange(undefined);
              // Der Satz "… wird mit dem Kontakt angelegt." stimmt nicht mehr; der virtuelle Cursor faende ihn sonst.
              setAnsage('');
              setFokus('anbieten');
            }}
          >
            Doch nicht anlegen
          </Button>
        </div>
        {angabenNeu}
        {leitungNeu}
      </div>
    );
  } else if (aktuell !== undefined && aktuell.organisationen.length > 0) {
    const ueberschrift = worksForHitsHeading(aktuell.organisationen.length);
    karte = (
      <div className={styles.karteSpalte}>
        <Body1 aria-hidden="true">{ueberschrift}</Body1>
        <ul className={styles.treffer} aria-label={ueberschrift.replace(/:$/, '')}>
          {aktuell.organisationen.map((eintrag) => {
            const caption = worksForHitCaption(eintrag);
            return (
              <li key={eintrag.partyId} className={styles.zeile}>
                <span className={styles.karteText}>
                  <Body1>
                    <strong>{eintrag.displayName}</strong>
                  </Body1>
                  {caption === undefined ? undefined : <Caption1 className={styles.gedaempft}>{caption}</Caption1>}
                </span>
                <Button
                  appearance="secondary"
                  size="small"
                  type="button"
                  disabled={disabled}
                  aria-label={`${eintrag.displayName} wählen`}
                  onClick={() => waehlen(eintrag)}
                >
                  Wählen
                </Button>
              </li>
            );
          })}
        </ul>
        {aktuell.mehr ? (
          <Caption1 className={styles.gedaempft}>{WORKS_FOR_MORE_HITS}</Caption1>
        ) : (
          <div>
            <Button
              ref={(knoten: HTMLButtonElement | null) => {
                anbietenRef.current = knoten;
              }}
              appearance="subtle"
              type="button"
              className={styles.umbrechend}
              disabled={disabled}
              onClick={() => neuAnlegen(aktuell.begriff)}
            >
              {noneOfTheseLabel(aktuell.begriff)}
            </Button>
          </div>
        )}
      </div>
    );
  } else if (aktuell !== undefined) {
    karte = (
      <div className={styles.karte}>
        <div className={styles.karteText}>
          <Body1>{WORKS_FOR_NO_MATCH}</Body1>
          {aktuell.mehr ? <Caption1 className={styles.gedaempft}>{WORKS_FOR_MORE_HITS}</Caption1> : undefined}
        </div>
        {aktuell.mehr ? undefined : (
          <Button
            ref={(knoten: HTMLButtonElement | null) => {
              anbietenRef.current = knoten;
            }}
            appearance="secondary"
            type="button"
            className={styles.umbrechend}
            disabled={disabled}
            onClick={() => neuAnlegen(aktuell.begriff)}
          >
            {createOrganizationLabel(aktuell.begriff)}
          </Button>
        )}
      </div>
    );
  }

  return mitAnsage(
    <div>
      <Field
        label="Arbeitet für"
        hint="Name der Organisation, mindestens drei Zeichen. Kann leer bleiben."
        validationMessage={sucheFehler ?? error}
        validationState={sucheFehler !== undefined || error !== null ? 'error' : 'none'}
      >
        <Input
          id={WORKS_FOR_SEARCH_INPUT_ID}
          type="search"
          value={begriff}
          disabled={disabled}
          onChange={(_event, data) => {
            setBegriff(data.value);
            if (sucheFehler !== undefined) setSucheFehler(undefined);
            // Eine laufende Suche galt dem alten Text: ihre Antwort darf nichts mehr setzen, auch kein "Neu".
            if (sucht && data.value.trim() !== gesucht) {
              laufRef.current += 1;
              setSucht(false);
              setAnsage('');
            }
            // Ein anderer Text ist eine andere Organisation: das Neue faellt weg, bis neu gesucht ist.
            if (neu !== undefined && data.value.trim() !== neu) onNeuChange(undefined);
          }}
          onBlur={sucheBeiAenderung}
          onKeyDown={(event) => {
            // Enter sucht, statt den Kontakt anzulegen: im Feld ist "Suchen" die naheliegende Handlung.
            if (event.key !== 'Enter') return;
            event.preventDefault();
            if (begriff.trim() !== gesucht) void suchen(begriff);
          }}
        />
      </Field>
      {karte}
    </div>,
  );
}
